import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import type { PortalDeliverableApproval } from '../../types';
import { formatPortalApprovalCaption } from '../../utils/portalDeliverableApprovalService';
import './PortalHtmlViewerModal.css';

export interface PortalHtmlViewerModalProps {
  url: string;
  title: string;
  approval?: PortalDeliverableApproval | null;
  /** Set when this 3D model is the same upload on more than one structure group. */
  sharedModelNote?: string;
  /** Present when this file is one of several attached to the same approval. */
  onAdvanceToNextFile?: () => void;
  /** Present on the last file of an approval, opens the initials modal. */
  onApprove?: () => void;
  onClose: () => void;
}

const PortalHtmlViewerModal: React.FC<PortalHtmlViewerModalProps> = ({
  url,
  title,
  approval,
  sharedModelNote,
  onAdvanceToNextFile,
  onApprove,
  onClose,
}) => {
  const caption = formatPortalApprovalCaption(approval);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    const frame = frameRef.current;
    const attach = () => {
      try {
        const win = frame?.contentWindow;
        if (!win) return;
        win.removeEventListener('keydown', onKey, true);
        win.addEventListener('keydown', onKey, true);
      } catch {
        // A cross-origin frame keeps its own keys.
      }
    };
    attach();
    frame?.addEventListener('load', attach);
    return () => {
      document.removeEventListener('keydown', onKey);
      frame?.removeEventListener('load', attach);
      try {
        frame?.contentWindow?.removeEventListener('keydown', onKey, true);
      } catch {
        /* ignore */
      }
    };
  }, [onClose]);

  return (
    <div className="portal-html-viewer-overlay" role="presentation" onClick={onClose}>
      <div
        className="portal-html-viewer-shell"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={event => event.stopPropagation()}
      >
        <header className="portal-html-viewer-header">
          <div className="portal-html-viewer-heading">
            <h2>{title}</h2>
            {sharedModelNote ? <p className="portal-html-viewer-shared" role="status">{sharedModelNote}</p> : null}
            {caption ? <p className="portal-html-viewer-approval">{caption}</p> : null}
          </div>
          <button type="button" className="portal-html-viewer-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <iframe
          ref={frameRef}
          className="portal-html-viewer-frame"
          title={title}
          src={url}
          sandbox="allow-scripts allow-same-origin allow-popups allow-pointer-lock allow-fullscreen"
          allow="fullscreen"
        />
        {onAdvanceToNextFile || onApprove ? (
          <footer className="portal-html-viewer-footer">
            {onAdvanceToNextFile ? (
              <button type="button" className="portal-html-viewer-advance" onClick={onAdvanceToNextFile}>
                Advance to next file
              </button>
            ) : null}
            {onApprove ? (
              <button type="button" className="portal-html-viewer-advance" onClick={onApprove}>
                Approve
              </button>
            ) : null}
          </footer>
        ) : null}
      </div>
    </div>
  );
};

export default PortalHtmlViewerModal;
